import { useCallback, useState, type MutableRefObject } from 'react';
import { ArrowSquareOutIcon, CaretDownIcon, SquaresFourIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { Tip } from '~/components/ui/tip';
import { Segmented } from '~/components/ui/segmented';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import type { CanvasItem, CanvasItemKind } from './collect-items';
import type { CanvasView } from './canvas-storage';
import type { CanvasFlush } from './canvas-send-gate';
import { CanvasFrame } from './canvas-frame';
import { changesToggle } from './changes-gate';
import { useDocumentChanges } from './use-document-changes';
import { CanvasDownloadButton } from './canvas-download-button';
import { useDocumentDetail } from '~/hooks/queries/use-documents';
import { ArtifactEditor } from './canvas-artifact-editor';
import { artifactSurface, frameKeepsReloadingReason } from './artifact-edit-logic';
import { useFrameVersion } from './artifact-updated';
import { artifactDetailEnabled, useArtifactDetail, type ArtifactDetail } from '~/hooks/queries/use-artifacts';
import { CanvasSaveStatus, DocumentCanvas, FileCanvas, type CanvasSaveStatus as SaveStatus } from './canvas-document';

export const CANVAS_KIND_LABEL: Record<CanvasItemKind, string> = {
  document: 'Document',
  sketch: 'Sketch',
  deck: 'Slides',
  file: 'Markdown',
};

/**
 * Where "Open in new tab" goes: a document opens in the Files screen
 * (`/files/doc/:filename`, the route the Files list itself links to); a
 * Markdown file opens in the Files screen by id (`/files?id=`, where the file
 * cards navigate); a sketch or deck opens its own served page.
 */
export function canvasNewTabHref(item: Pick<CanvasItem, 'kind' | 'id'> & { path?: string | null }): string {
  if (item.kind === 'document') return `/files/doc/${encodeURIComponent(item.id)}`;
  if (item.kind === 'file') return `/files?id=${encodeURIComponent(item.id)}`;
  return item.path || `/apps/${encodeURIComponent(item.id)}/`;
}

const VIEW_OPTIONS: { value: CanvasView; label: string; testId: string }[] = [
  { value: 'preview', label: 'Preview', testId: 'canvas-view-preview' },
  { value: 'changes', label: 'Changes', testId: 'canvas-view-changes' },
  { value: 'edit', label: 'Edit', testId: 'canvas-view-edit' },
];

/**
 * FRONT-2598 / FRONT-2743 — whether a sketch or a deck can be edited by hand.
 * `editable` opens the editor surface at once (no switch); a deck then also offers "Arrange slides".
 * `blocked` keeps the plain page and, for a deck, shows "Arrange slides" disabled WITH its reason
 * (disabled iff there is a reason); `none` (not the owner, or nothing read yet) shows neither,
 * exactly as before manual editing existed.
 */
export type ArtifactEditState =
  | { state: 'none' }
  | { state: 'editable' }
  | { state: 'blocked'; reason: string };

export function artifactEditState(data: Pick<ArtifactDetail, 'editable' | 'editBlockedReason'> | undefined): ArtifactEditState {
  if (!data) return { state: 'none' };
  if (data.editable) return { state: 'editable' };
  if (data.editBlockedReason) return { state: 'blocked', reason: data.editBlockedReason };
  return { state: 'none' };
}

/**
 * FRONT-2743 — the deck-only "Arrange slides" toggle: absent for a sketch, a non-owner and no answer;
 * present and enabled when editable; present and disabled with the blocked reason as its tooltip.
 * Disabled iff there is a reason, so the button and its explanation are one decision.
 */
export function arrangeSlidesGate(
  kind: CanvasItemKind | null | undefined,
  edit: ArtifactEditState,
  frameUnstable = false,
): { disabled: false; tooltip: string } | { disabled: true; tooltip: string } | null {
  if (kind !== 'deck') return null;
  // The editor was given up on (the page keeps reloading): the plain frame is shown, so nothing to arrange.
  if (frameUnstable) return { disabled: true, tooltip: frameKeepsReloadingReason('deck') };
  if (edit.state === 'editable') return { disabled: false, tooltip: 'Arrange slides' };
  if (edit.state === 'blocked') return { disabled: true, tooltip: edit.reason };
  return null;
}

/** Why a kept draft cannot be saved: the server's own reason when it gave one, else a plain sentence. */
export const DRAFT_UNEDITABLE_REASON = 'This item can no longer be edited here, so your unsaved changes cannot be saved. Discard them to continue.';

export function draftBlockedReason(edit: ArtifactEditState): string | null {
  if (edit.state === 'editable') return null;
  return edit.state === 'blocked' ? `${edit.reason} Your unsaved changes cannot be saved; discard them to continue.` : DRAFT_UNEDITABLE_REASON;
}

/**
 * Which surface a sketch or deck gets, and why a kept draft cannot be saved. `dirty` is NOT gated on
 * the item being editable: a draft with unsaved text stays mounted (locked, with the reason) when the
 * item has just stopped being editable, so the typed text is never silently thrown away.
 */
export function planArtifactEditor(input: { hasDetail: boolean; edit: ArtifactEditState; dirty: boolean }) {
  const editable = input.hasDetail && input.edit.state === 'editable';
  const dirty = input.hasDetail && input.dirty;
  const surface = artifactSurface({ editable, dirty });
  const showEditor = surface === 'editor';
  return { surface, showEditor, blockedReason: showEditor && !editable ? draftBlockedReason(input.edit) : null };
}

/**
 * Where a sketch or deck is in its detail lookup. `pending` renders a neutral placeholder so the page is
 * not loaded twice (plain frame, then the edit frame once the answer says editable); `failed` falls back to
 * the plain frame so a failing detail never blanks the panel. A failure is declared as soon as the first
 * attempt has failed (`failureCount > 0`) or the query is paused (offline), not after the final retry.
 */
export function artifactDetailState(
  detailKind: string | null,
  q: { isPending: boolean; isError: boolean; data: unknown; failureCount?: number; fetchStatus?: string },
): 'none' | 'pending' | 'failed' | 'ready' {
  if (!detailKind) return 'none';
  if (q.data) return 'ready';
  if (q.isError || (q.failureCount ?? 0) > 0 || q.fetchStatus === 'paused') return 'failed';
  return q.isPending ? 'pending' : 'failed';
}

export interface CanvasPanelProps {
  sessionId: string;
  items: CanvasItem[];
  /** Already resolved against `items` (never a key that no longer exists). */
  activeKey: string | null;
  view: CanvasView;
  onViewChange: (view: CanvasView) => void;
  onSelect: (key: string) => void;
  onHide: () => void;
  /** A draft with unsaved changes is open in a text editor — a document, a Markdown file, a sketch or a deck (blocks Escape-to-close). */
  onDirtyChange: (dirty: boolean) => void;
  /** Where the mounted editor (document, file, sketch or deck) registers its save-before-send flush (canvas-send-gate.ts). */
  flushRef?: MutableRefObject<CanvasFlush | null>;
  /** Something changed while an unsaved draft kept the active item in place: dot on the switcher. */
  switcherDot?: boolean;
}

export function CanvasPanel({
  sessionId,
  items,
  activeKey,
  view,
  onViewChange,
  onSelect,
  onHide,
  onDirtyChange,
  flushRef,
  switcherDot = false,
}: CanvasPanelProps) {
  const active = items.find((i) => i.key === activeKey) ?? null;
  // A document and a Markdown file share the viewer, the editor and the Preview | Edit switch.
  const isText = active?.kind === 'document' || active?.kind === 'file';
  // FRONT-2669: the Changes toggle's disabled state and its tooltip are derived from ONE reason.
  // FRONT-2669: Download takes the SAVED body (the detail query), and the tooltip says so while a draft is unsaved.
  const savedDoc = useDocumentDetail(active?.kind === 'document' ? active.id : null);
  const changes = useDocumentChanges(active?.kind === 'document' ? active.id : null);
  const changesGateState = changesToggle(changes.availability);
  // A Markdown file has no version history: it never offers Changes (no disabled tab with a wrong reason),
  // and a stored `changes` view carried over from a document reads as Preview there.
  const offersChanges = active?.kind === 'document';
  const viewOptions = VIEW_OPTIONS.filter((o) => o.value !== 'changes' || offersChanges).map((o) =>
    o.value === 'changes' ? { ...o, disabled: changesGateState.disabled, tooltip: changesGateState.reason ?? undefined } : o,
  );
  // "Saved" is per item: a status for one document must not follow you to another.
  const [status, setStatus] = useState<{ key: string | null; value: SaveStatus }>({ key: null, value: 'idle' });
  const saveStatus: SaveStatus = status.key === activeKey ? status.value : 'idle';
  // A sketch or a deck asks the server whether the person may edit it by hand (a document
  // or file never does); no answer yet, or not the owner, leaves the V1 viewer untouched.
  const detailKind = active && artifactDetailEnabled(active.kind) ? active.kind : null;
  const artifact = useArtifactDetail(detailKind, detailKind ? active!.id : null);
  const edit = detailKind ? artifactEditState(artifact.data) : ({ state: 'none' } as ArtifactEditState);
  // A sketch or deck editor with an unsaved draft stays mounted when the item stops being editable, so
  // the draft, the Escape guard and the save-before-send flush all survive. `dirtyKey`
  // says WHICH item reported dirty, so a draft on one item never keeps another item's editor alive.
  const [dirtyKey, setDirtyKey] = useState<string | null>(null);
  // Stable between renders: the editor re-runs its dirty effect when this changes, and a fresh function
  // each render would report "clean" then "dirty" on every commit.
  const handleEditorDirty = useCallback(
    (dirty: boolean) => {
      setDirtyKey((k) => (dirty ? activeKey : k === activeKey ? null : k));
      onDirtyChange(dirty);
    },
    [activeKey, onDirtyChange],
  );
  // Only a document or Markdown file has a view switch; a sketch or deck never reads the stored view.
  const showSwitch = isText;
  // A Markdown file has no Changes view: a stored `changes` carried over from a document reads as Preview.
  const shownView: CanvasView = !offersChanges && view === 'changes' ? 'preview' : view;
  // "Arrange slides" (deck only): open state is local and keyed by the item, so it starts closed and
  // closes when the active item changes. Never persisted.
  const [arrangeKey, setArrangeKey] = useState<string | null>(null);
  // A self-reloading page the editor gave up on while the draft was clean: that item shows the plain page
  // instead (a locked editor would leave the owner nothing usable). Tied to the item and its version.
  const [frameFallback, setFrameFallback] = useState<{ key: string; version: string } | null>(null);
  // Both are reset the moment the active item changes (set during render, so there is no frame with the
  // old value): going deck A -> B -> A must show A's slide list closed and its editor tried afresh.
  const [lastKey, setLastKey] = useState<string | null>(activeKey);
  if (lastKey !== activeKey) {
    setLastKey(activeKey);
    setArrangeKey(null);
    setFrameFallback(null);
  }
  // The version an `artifact-updated` event announced (the one CanvasFrame reloads on) is part of what the
  // fallback is tied to, so a live update retries the editor. Called unconditionally (hooks order).
  const announcedVersion = useFrameVersion(active?.kind === 'deck' ? 'deck' : 'sketch', active?.id ?? '');
  const itemVersion = `${active?.updatedAt ?? active?.revision ?? ''}:${artifact.data?.updatedAt ?? ''}:${announcedVersion ?? ''}`;
  const frameUnstable = !!active && frameFallback?.key === active.key && frameFallback.version === itemVersion;
  const handleFrameUnstable = useCallback(() => {
    if (activeKey) setFrameFallback({ key: activeKey, version: itemVersion });
  }, [activeKey, itemVersion]);
  const draftUnsaved = dirtyKey !== null && dirtyKey === activeKey;
  const plan = planArtifactEditor({
    hasDetail: !!detailKind && !!artifact.data,
    edit,
    dirty: dirtyKey === activeKey,
  });
  const { blockedReason } = plan;
  const detailState = artifactDetailState(detailKind, artifact);
  const showEditor = plan.showEditor && !frameUnstable;
  // One value for the pressed state and the strip: a deck that is blocked or not the owner's shows
  // neither a strip nor a pressed button (the draft itself stays in the editor).
  const arrangeOpen = active?.kind === 'deck' && arrangeKey === active.key && showEditor && edit.state === 'editable';
  const arrangeGate = arrangeSlidesGate(active?.kind, edit, frameUnstable);

  return (
    <div
      data-testid="canvas-panel"
      data-canvas-region="panel"
      className="flex min-h-0 min-w-0 flex-col border-l border-border bg-canvas-surface"
    >
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-card px-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="canvas-item-switcher"
              aria-label="Switch Canvas item"
              className="min-w-0 flex-1 justify-start gap-1.5"
            >
              <span className="min-w-0 truncate">{active?.title ?? 'Canvas'}</span>
              {switcherDot && (
                <span
                  data-testid="canvas-switcher-dot"
                  role="img"
                  aria-label="Something new in Canvas"
                  className="size-1.5 shrink-0 rounded-full bg-primary"
                />
              )}
              <CaretDownIcon size={12} aria-hidden className="shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            data-testid="canvas-item-menu"
            className="max-h-72 w-max min-w-[min(var(--radix-dropdown-menu-trigger-width),min(32rem,90vw))] max-w-[min(32rem,90vw)] overflow-y-auto"
          >
            {items.map((item) => (
              <DropdownMenuItem
                key={item.key}
                data-testid={`canvas-item-${item.key}`}
                onSelect={() => onSelect(item.key)}
                className="items-start gap-3"
              >
                <span data-testid={`canvas-item-title-${item.key}`} className="min-w-0 flex-1 whitespace-normal break-words">
                  {item.title}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{CANVAS_KIND_LABEL[item.kind]}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {active && (
          <span data-testid="canvas-kind" className="shrink-0 text-xs text-muted-foreground">
            {CANVAS_KIND_LABEL[active.kind]}
          </span>
        )}
        <CanvasSaveStatus status={saveStatus} />
        {showSwitch && (
          <Segmented<CanvasView>
            size="sm"
            aria-label="Document view"
            value={shownView}
            onChange={onViewChange}
            options={viewOptions}
          />
        )}
        {arrangeGate && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="canvas-arrange-slides"
            aria-label="Arrange slides"
            aria-pressed={arrangeOpen}
            disabled={arrangeGate.disabled}
            tooltip={arrangeGate.tooltip}
            onClick={() => setArrangeKey(arrangeOpen ? null : active!.key)}
            className="shrink-0 gap-1.5"
          >
            <SquaresFourIcon size={16} aria-hidden />
            <span className="hidden md:inline">Arrange slides</span>
          </Button>
        )}
        {active && (
          <CanvasDownloadButton item={active} savedContent={savedDoc.data?.content ?? null} draftUnsaved={draftUnsaved} />
        )}
        {active && (
          <Tip label="Open in new tab">
            <a
              href={canvasNewTabHref(active)}
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Open in new tab"
              data-testid="canvas-open-new-tab"
              className="inline-flex size-8 items-center justify-center rounded-md text-foreground hover:bg-muted"
            >
              <ArrowSquareOutIcon size={16} />
            </a>
          </Tip>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          tooltip="Hide Canvas"
          aria-label="Hide Canvas"
          data-testid="canvas-hide"
          onClick={onHide}
        >
          <XIcon size={16} />
        </Button>
      </div>
      <div className={`flex min-h-0 flex-1 flex-col ${isText ? 'overflow-y-auto' : 'overflow-hidden'}`}>
        {active?.kind === 'document' && (
          <DocumentCanvas
            sessionId={sessionId}
            filename={active.id}
            view={view}
            onStatusChange={(value) => setStatus({ key: activeKey, value })}
            onDirtyChange={handleEditorDirty}
            flushRef={flushRef}
          />
        )}
        {active?.kind === 'file' && (
          <FileCanvas
            sessionId={sessionId}
            fileId={active.id}
            view={shownView}
            onStatusChange={(value) => setStatus({ key: activeKey, value })}
            onDirtyChange={handleEditorDirty}
            flushRef={flushRef}
          />
        )}
        {(edit.state === 'blocked' || (blockedReason && edit.state === 'none')) && (
          <p
            role="status"
            data-testid="canvas-edit-blocked"
            className="shrink-0 border-b border-border bg-muted px-4 py-2 text-xs text-muted-foreground"
          >
            {edit.state === 'blocked' ? edit.reason : DRAFT_UNEDITABLE_REASON}
          </p>
        )}
        {frameUnstable && detailKind && (
          <p role="status" data-testid="canvas-frame-unstable" className="shrink-0 border-b border-border bg-muted px-4 py-2 text-xs text-muted-foreground">
            {frameKeepsReloadingReason(detailKind)}
          </p>
        )}
        {showEditor && detailKind && artifact.data && (
          <ArtifactEditor
            key={`${sessionId}:${active!.key}`}
            sessionId={sessionId}
            kind={detailKind}
            slug={active!.id}
            title={active!.title}
            detail={artifact.data}
            arrangeOpen={arrangeOpen}
            blockedReason={blockedReason}
            onStatusChange={(value) => setStatus({ key: activeKey, value })}
            onDirtyChange={handleEditorDirty}
            onFrameUnstable={handleFrameUnstable}
            flushRef={flushRef}
          />
        )}
        {detailState === 'pending' && <div data-testid="canvas-frame-pending" className="flex min-h-0 flex-1" />}
        {active && active.kind !== 'document' && active.kind !== 'file' && !showEditor && detailState !== 'pending' && (
          <CanvasFrame
            kind={active.kind}
            slug={active.id}
            title={active.title}
            cacheKey={active.updatedAt ?? active.revision}
            path={active.path}
          />
        )}
      </div>
    </div>
  );
}
